import React, { useState, useEffect } from 'react';
import { Appointment } from '../api/appointment';
import { Calendar, Clock, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';

const fetchUserAppointments = async (): Promise<Appointment[]> => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/appointments/user`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch appointments');
  }

  return response.json();
};

const formatDate = (date: string): string => {
  return new Date(date).toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC'
  });
};

const formatTime = (date: string): string => {
  return new Date(date).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC'
  });
};

const cancelAppointment = async (appointmentId: string): Promise<void> => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/appointments/${appointmentId}/cancel`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to cancel appointment');
  }
};

const AppointmentCard: React.FC<{
  appointment: Appointment;
  isPast?: boolean;
  onCancel: () => void;
}> = ({ appointment, isPast = false, onCancel }) => {
  const statusColor = {
    scheduled: 'bg-blue-50 text-blue-700',
    completed: 'bg-green-50 text-green-700',
    cancelled: 'bg-red-50 text-red-700',
  }[appointment.status];

  const [isCancelling, setIsCancelling] = useState(false);

  const handleCancel = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler ce rendez-vous ?')) {
      return;
    }

    try {
      setIsCancelling(true);
      await cancelAppointment(appointment.id);
      onCancel();
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      alert(err instanceof Error ? err.message : 'Erreur lors de l\'annulation');
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium">Dr. {appointment.therapist.firstName}</h3>
        <span className={`text-sm px-2 py-1 rounded-full ${statusColor}`}>
          {appointment.status === 'scheduled' && 'À venir'}
          {appointment.status === 'completed' && 'Terminé'}
          {appointment.status === 'cancelled' && 'Annulé'}
        </span>
      </div>

      <div className="space-y-2 text-gray-600">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>{formatDate(appointment.date)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>{formatTime(appointment.date)}</span>
        </div>
      </div>

      {!isPast && appointment.status === 'scheduled' && (
        <div className="mt-4 flex justify-end">
          <Button 
            variant="outline" 
            className="text-red-600 hover:text-red-700"
            onClick={handleCancel}
            disabled={isCancelling}
          >
            {isCancelling ? 'Annulation...' : 'Annuler'}
          </Button>
        </div>
      )}
    </div>
  );
};

export const AppointmentsList = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAppointments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchUserAppointments();
      setAppointments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load appointments');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAppointments();
  }, []);

  const now = new Date();
  const upcomingAppointments = appointments.filter(
    app => new Date(app.date) > now && app.status !== 'cancelled'
  ).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const pastAppointments = appointments.filter(
    app => new Date(app.date) <= now || app.status === 'cancelled'
  ).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00c4cc]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 animate-fadeIn">
      <h1 className="text-2xl font-bold mb-6">Mes rendez-vous</h1>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00c4cc]"></div>
        </div>
      ) : (
        <div className="space-y-8">
          <section className="transform transition-all duration-300">
            <h2 className="text-lg font-semibold mb-4">Prochains rendez-vous</h2>
            {upcomingAppointments.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                Aucun rendez-vous à venir
              </p>
            ) : (
              upcomingAppointments.map(appointment => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  onCancel={loadAppointments}
                />
              ))
            )}
          </section>

          <section className="transform transition-all duration-300">
            <h2 className="text-lg font-semibold mb-4">Historique</h2>
            {pastAppointments.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                Aucun rendez-vous passé
              </p>
            ) : (
              pastAppointments.map(appointment => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  isPast
                  onCancel={loadAppointments}
                />
              ))
            )}
          </section>
        </div>
      )}
    </div>
  );
};
